/**
 * Things villagers finished building. The walls, paths and bridges are already
 * placed tiles, so the client only adds what tiles cannot show: a pyramid roof
 * and a threshold in the builder's colour for a hut, logs and a warm glow for a
 * campfire. Blueprints still in progress get a ghost outline instead.
 */
import * as THREE from 'three';
import type { BuildJob, Structure, Villager } from '@village/shared';
import { STRUCTURE_COLORS, mixHex } from '../palette';
import type { Terrain } from './terrain';

export interface Fire {
  flame: THREE.Mesh;
  light: THREE.PointLight;
  phase: number;
}

function mat(color: string, roughness = 0.9): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness, metalness: 0, flatShading: true });
}

function box(w: number, h: number, d: number, color: string): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

export class StructureLayer {
  readonly group = new THREE.Group();
  readonly fires: Fire[] = [];
  private views = new Map<string, THREE.Group>();

  constructor(private terrain: Terrain, private colorOf: (villagerId: string) => string) {
    this.group.name = 'structures';
  }

  sync(structures: Structure[]): void {
    const seen = new Set<string>();
    for (const s of structures) {
      seen.add(s.id);
      if (!this.views.has(s.id)) this.add(s);
    }
    for (const id of [...this.views.keys()]) if (!seen.has(id)) this.remove(id);
  }

  add(s: Structure): void {
    if (this.views.has(s.id)) return;
    const color = this.colorOf(s.builtBy);
    let g: THREE.Group | null = null;
    if (s.kind === 'hut') g = this.buildHut(s, color);
    else if (s.kind === 'campfire') g = this.buildCampfire(s);
    // fence, bridge and path are nothing but the tiles the villager placed
    if (!g) return;
    g.userData = { structureId: s.id, kind: s.kind, builtBy: s.builtBy, pickable: true };
    g.name = `structure:${s.id}`;
    this.views.set(s.id, g);
    this.group.add(g);
  }

  remove(id: string): void {
    const g = this.views.get(id);
    if (!g) return;
    this.group.remove(g);
    const fire = g.userData.fire as Fire | undefined;
    if (fire) {
      const i = this.fires.indexOf(fire);
      if (i >= 0) this.fires.splice(i, 1);
    }
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mm = m.material as THREE.Material | undefined;
      mm?.dispose();
    });
    this.views.delete(id);
  }

  private topOf(s: { x: number; z: number; w: number; d: number }): number {
    let top = 0;
    for (let z = s.z; z < s.z + s.d; z++) {
      for (let x = s.x; x < s.x + s.w; x++) top = Math.max(top, this.terrain.surfaceY(x, z));
    }
    return top;
  }

  private buildHut(s: Structure, color: string): THREE.Group {
    const g = new THREE.Group();
    const cx = s.x + (s.w - 1) / 2;
    const cz = s.z + (s.d - 1) / 2;
    const top = this.topOf(s);

    const roof = new THREE.Mesh(
      new THREE.ConeGeometry(Math.max(s.w, s.d) * 0.74, 1.0, 4, 1),
      mat(mixHex(color, '#FFFFFF', 0.28)),
    );
    roof.rotation.y = Math.PI / 4;
    roof.castShadow = true;
    roof.receiveShadow = true;
    roof.position.set(cx, top + 0.52, cz);
    g.add(roof);

    // a band in the builder's colour where the roof meets the walls
    const band = box(s.w + 0.14, 0.09, s.d + 0.14, color);
    band.position.set(cx, top + 0.03, cz);
    g.add(band);

    // threshold: a plate on the door cell plus two posts marking the gap
    const dx = Math.sign(s.door.x - cx);
    const dz = Math.sign(s.door.z - cz);
    const doorY = this.terrain.surfaceY(s.door.x, s.door.z);
    const plate = box(0.72, 0.06, 0.72, mixHex(color, '#FFFFFF', 0.5));
    plate.position.set(s.door.x, doorY + 0.03, s.door.z);
    g.add(plate);
    const postOffset = 0.34;
    for (const side of [-1, 1]) {
      const post = box(0.1, 0.55, 0.1, mixHex(color, '#3B3A48', 0.12));
      post.position.set(
        s.door.x - dx * 0.5 + (dz !== 0 ? side * postOffset : 0),
        doorY + 0.28,
        s.door.z - dz * 0.5 + (dx !== 0 ? side * postOffset : 0),
      );
      g.add(post);
    }
    return g;
  }

  private buildCampfire(s: Structure): THREE.Group {
    const g = new THREE.Group();
    const cx = s.x + (s.w - 1) / 2;
    const cz = s.z + (s.d - 1) / 2;
    const top = this.topOf(s);
    g.position.set(cx, top, cz);

    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const stone = box(0.17, 0.12, 0.17, STRUCTURE_COLORS.stone);
      stone.position.set(Math.cos(a) * 0.4, 0.06, Math.sin(a) * 0.4);
      stone.rotation.y = a;
      g.add(stone);
    }
    for (let i = 0; i < 3; i++) {
      const log = box(0.58, 0.1, 0.1, STRUCTURE_COLORS.woodDark);
      log.position.y = 0.1;
      log.rotation.y = (i / 3) * Math.PI;
      log.rotation.z = 0.12;
      g.add(log);
    }
    const flame = new THREE.Mesh(
      new THREE.ConeGeometry(0.2, 0.42, 5),
      new THREE.MeshStandardMaterial({
        color: new THREE.Color(STRUCTURE_COLORS.ember),
        emissive: new THREE.Color('#FF8A4C'),
        emissiveIntensity: 1.6,
        roughness: 0.6,
        flatShading: true,
      }),
    );
    flame.position.y = 0.36;
    g.add(flame);

    const light = new THREE.PointLight(new THREE.Color('#FFA06B'), 5.5, 8, 2);
    light.position.y = 0.6;
    g.add(light);

    const fire: Fire = { flame, light, phase: this.fires.length * 1.7 };
    g.userData.fire = fire;
    this.fires.push(fire);
    return g;
  }
}

/** Translucent outline over the footprint of a blueprint under construction. */
export class GhostLayer {
  readonly group = new THREE.Group();
  private views = new Map<string, { root: THREE.Group; key: string }>();

  constructor(private terrain: Terrain) {
    this.group.name = 'ghosts';
  }

  sync(villagers: Villager[]): void {
    const seen = new Set<string>();
    for (const v of villagers) {
      if (!v.build) continue;
      seen.add(v.id);
      const key = ghostKey(v.build);
      const existing = this.views.get(v.id);
      if (existing && existing.key === key) continue;
      if (existing) this.drop(v.id);
      this.views.set(v.id, { root: this.build(v.build, v.color), key });
      this.group.add(this.views.get(v.id)!.root);
    }
    for (const id of [...this.views.keys()]) if (!seen.has(id)) this.drop(id);
  }

  private drop(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    this.group.remove(view.root);
    view.root.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mm = m.material as THREE.Material | undefined;
      mm?.dispose();
    });
    this.views.delete(id);
  }

  private build(job: BuildJob, color: string): THREE.Group {
    const g = new THREE.Group();
    const cx = job.x + (job.w - 1) / 2;
    const cz = job.z + (job.d - 1) / 2;
    let top = 0;
    for (let z = job.z; z < job.z + job.d; z++) {
      for (let x = job.x; x < job.x + job.w; x++) top = Math.max(top, this.terrain.surfaceY(x, z));
    }
    const h = job.blueprint === 'hut' ? 1.1 : 0.4;
    const geom = new THREE.BoxGeometry(job.w, h, job.d);
    const fill = new THREE.Mesh(
      geom,
      new THREE.MeshBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0.16, depthWrite: false }),
    );
    fill.position.set(cx, top + h / 2, cz);
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geom),
      new THREE.LineBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0.55 }),
    );
    edges.position.copy(fill.position);
    g.add(fill, edges);
    g.userData = { ghost: true, pickable: false };
    return g;
  }
}

const ghostKey = (b: BuildJob) => `${b.blueprint}:${b.x}:${b.z}:${b.w}:${b.d}`;

/** Walk up from a picked mesh to the structure group that owns it. */
export function structureOf(obj: THREE.Object3D | null): THREE.Object3D | null {
  let cur: THREE.Object3D | null = obj;
  while (cur) {
    if (cur.userData?.structureId) return cur;
    cur = cur.parent;
  }
  return null;
}
